import { Field } from '@/components/Fields';
import { EditIcon } from '@/components/icons/Icons';
import { useErrorIndicator } from '@/hooks';
import { ArrowUpIcon, SmallCloseIcon } from '@chakra-ui/icons';
import { FormControl, HStack, IconButton, Text, useToast } from '@chakra-ui/react';
import { Form, Formik, FormikHelpers } from 'formik';
import { useState } from 'react';
import * as Yup from 'yup';

interface EditOrgFormProps {
  userId: string;
  onSubmit: (userId: string, newOrg: string) => Promise<void>;
  initialOrg: string;
}

export const EditOrgForm: React.FC<EditOrgFormProps> = ({ userId, onSubmit, initialOrg }) => {
  const [isEditing, setIsEditing] = useState(false);
  const toast = useToast();
  const { showError } = useErrorIndicator();

  const handleSubmit = async (
    values: { organization: string },
    { setSubmitting }: FormikHelpers<{ organization: string }>,
  ) => {
    if (values.organization === initialOrg) {
      setIsEditing(false);
      return;
    }
    setSubmitting(true);
    try {
      await onSubmit(userId, values.organization);
      setIsEditing(false);
      toast({
        title: 'Organization updated',
        status: 'success',
        duration: 3000,
      });
    } catch (error) {
      showError('Error updating users organization');
    } finally {
      setSubmitting(false);
    }
  };

  const UpdateKbIdSchema = Yup.object().shape({
    organization: Yup.string().required('Required'),
  });

  return (
    <>
      {!isEditing && (
        <HStack>
          <Text>{initialOrg || 'None'}</Text>
          <EditIcon
            fill="white"
            cursor="pointer"
            aria-label="Edit Organization"
            onClick={() => setIsEditing(true)}
          />
        </HStack>
      )}
      {isEditing && (
        <Formik
          key={userId}
          initialValues={{
            organization: initialOrg,
          }}
          onSubmit={handleSubmit}
          validationSchema={UpdateKbIdSchema}
        >
          {({ handleSubmit, isSubmitting, isValid, dirty, errors, touched }) => (
            <Form onSubmit={handleSubmit}>
              <FormControl>
                <Field
                  type="text"
                  bg="white"
                  border="2px solid"
                  borderColor="gray.600"
                  placeholderFontSize="sm"
                  placeholderFontWeight="400"
                  placeholder={initialOrg}
                  name="organization"
                  errors={errors.organization}
                  touched={touched.organization}
                  fontSize="sm"
                  size="xs"
                  height="36px"
                  hideErrorMessage
                  pr={16}
                  px={3}
                  mb={0}
                  rightElementStyles={{ ml: '6px', mr: '16px' }}
                  rightElement={
                    <HStack>
                      <IconButton
                        type="submit"
                        size="xs"
                        aria-label="Submit Edit Organization"
                        icon={<ArrowUpIcon color="black" fill="black" />}
                        isDisabled={!isValid || !dirty || isSubmitting}
                        isLoading={isSubmitting}
                      />
                      <IconButton
                        type="button"
                        size="xs"
                        aria-label="Close Edit Organization"
                        icon={<SmallCloseIcon color="black" fill="black" />}
                        isDisabled={isSubmitting}
                        isLoading={isSubmitting}
                        onClick={() => setIsEditing(false)}
                      />
                    </HStack>
                  }
                />
              </FormControl>
            </Form>
          )}
        </Formik>
      )}
    </>
  );
};
